import merge from 'lodash/merge';
import { AnalyticsBrowser } from '@segment/analytics-next';
import ENVIRONMENT from '@/environment';

const { account, env, user } = window.globalProps;

const trackedEvents = new Set();

const newSegmentAnalytics = ENVIRONMENT.SEGMENT_JS_WRITE_KEY_NEW
  ? AnalyticsBrowser.load(
      {
        cdnURL: 'https://pa-events-cdn.bitrise.io',
        writeKey: ENVIRONMENT.SEGMENT_JS_WRITE_KEY_NEW,
      },
      {
        initialPageview: false,
        integrations: {
          'Segment.io': {
            apiHost: 'pa-events-api.bitrise.io/v1',
            protocol: 'https',
          },
        },
        obfuscate: true,
      },
    )
  : null;

if (user && newSegmentAnalytics) {
  newSegmentAnalytics.identify(user?.slug);
}

type SegmentEventProperties = {
  [key: string]: unknown;
  event_schema_version: number;
  is_known_user: boolean;
  initiator: 'user' | 'system';
  source_service_name: 'bitrise-website';
  source_service_version: string;
  tracking_type: 'server_side' | 'client_side' | 'client_to_server';
  event_scope: 'user' | 'app' | 'workspace';
  event_workspace_association: 'workspace_specific' | 'not_workspace_related' | 'implicit';
  event_type: 'core' | 'product' | 'interaction' | 'technical';
  page_url: string;
};

type SegmentEventContext = {
  [key: string]: unknown;
  workspace_slug: string;
};

const baseProperties: SegmentEventProperties = {
  event_schema_version: 1,
  event_scope: 'user',
  event_type: 'interaction',
  event_workspace_association: account ? 'workspace_specific' : 'implicit',
  initiator: 'user',
  is_known_user: false,
  page_url: window.location.href,
  source_service_name: 'bitrise-website',
  source_service_version: '',
  tracking_type: 'client_side',
};

const baseContext: SegmentEventContext = {
  workspace_slug: '',
};

const createMergedProps = (eventProps: Partial<SegmentEventProperties>) => {
  const mergedProps: SegmentEventProperties = merge({}, baseProperties, eventProps);

  if (user) {
    mergedProps.is_known_user = true;
  }

  if (account) {
    mergedProps.workspace_slug = account.slug;
  }

  if (env.WEBSITE_RELEASE_NUMBER && mergedProps) {
    mergedProps.source_service_version = String(env.WEBSITE_RELEASE_NUMBER);
  }

  return mergedProps;
};

const createMergedContext = (eventContext: Partial<SegmentEventContext>) => {
  const mergedContext: SegmentEventContext = merge({}, baseContext, eventContext);

  if (account) {
    mergedContext.workspace_slug = account.slug;
  }

  return mergedContext;
};

export const dataLayerPush = (eventName: string, props: Partial<SegmentEventProperties>) => {
  window.dataLayer?.push({
    event: eventName,
    ...props,
  });
};

export const segmentPage = (category?: string, pageName?: string) => {
  const mergedProps = createMergedProps({});
  const mergedContext = createMergedContext({
    app: {
      version: mergedProps.source_service_version,
    },
  });

  if (user && newSegmentAnalytics) {
    newSegmentAnalytics.page(category, pageName, mergedProps, mergedContext);
  }
};

export const segmentIdentify = (slug: string) => {
  if (newSegmentAnalytics) newSegmentAnalytics.identify(slug);
};

export const segmentTrack = (
  eventName: string,
  eventProps?: Partial<SegmentEventProperties>,
  eventContext?: Partial<SegmentEventContext>,
) => {
  const mergedProps = createMergedProps(eventProps || {});
  const mergedContext = createMergedContext(eventContext || {});

  // NOTE: in the future we might track anonym users but for now only logged in
  if (user && newSegmentAnalytics) {
    newSegmentAnalytics.track(eventName, mergedProps, mergedContext);

    dataLayerPush(eventName, mergedProps);
  }
};

export const segmentTrackOnce = (
  eventName: string,
  eventProps?: Partial<SegmentEventProperties>,
  eventContext?: Partial<SegmentEventContext>,
) => {
  const mergedProps = createMergedProps(eventProps || {});
  const mergedContext = createMergedContext(eventContext || {});

  // NOTE: in the future we might track anonym users but for now only logged in
  if (user && newSegmentAnalytics && !trackedEvents.has(eventName)) {
    newSegmentAnalytics.track(eventName, mergedProps, mergedContext);
    trackedEvents.add(eventName);

    dataLayerPush(eventName, mergedProps);
  }
};

segmentPage();
